import { twApi } from './../api';
import { PaginateResponse } from './../../Global.model';

import {
  GetExpenseByIdResponse,
  SearchOtherExpenseResponse,
  SearchOtherExpensesParams,
  CreateExpenseResponse,
  CreateExpenseParams,
  UpdateExpenseParams,
  UpdateExpenseResponse,
  OtherExpenseFilterParams
} from './otherExpense.model';
import { SearchFieldParams } from '..';

const otherExpenseApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getExpenseById: builder.query<GetExpenseByIdResponse, { id: string | null; token?: string }>({
      query: (arg) => {
        let url = `/v3/other-expenses/${arg.id}`;

        if (arg.token) {
          url = `/v3/other-expenses/t/${arg.token}/${arg.id}`;
        }

        return {
          url,
          method: 'GET'
        };
      },
      transformResponse: (response: { data: GetExpenseByIdResponse }) => response.data,
      providesTags: (result, error, arg) => [{ type: 'OtherExpenses' as const, id: arg.id! }]
    }),
    searchOtherExpenses: builder.query<
      PaginateResponse<SearchOtherExpenseResponse>,
      SearchOtherExpensesParams
    >({
      query: (arg) => {
        let url = '/v3/other-expenses/search';

        if (arg.token) {
          url = `v3/other-expenses/t/${arg.token}`;
        }

        return {
          url,
          method: arg.token ? 'GET' : 'POST',
          ...(!arg.token && { body: { ...arg.filters }, params: { ...arg.params } })
        };
      },
      transformResponse: (response: {
        data: SearchOtherExpenseResponse[];
        more_records: boolean;
        size: number;
        total: number;
      }) => ({
        content: response.data,
        more_records: response.more_records,
        total: response.total
      }),
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'WorkOrders' as const, id })),
              { type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' }
            ]
          : [{ type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' }]
    }),
    createOtherExpense: builder.mutation<CreateExpenseResponse, CreateExpenseParams>({
      query: (arg) => {
        let url = '/v3/other-expenses';
        if (arg.token) {
          url = `/v3/other-expenses/t/${arg.token}`;
        }

        return {
          url,
          method: 'POST',
          body: arg.body
        };
      },
      transformResponse: (response: CreateExpenseResponse) => ({
        data: response.data,
        message: response.message
      }),
      invalidatesTags: (result, error, data) => [
        { type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' },
        { type: 'Trips', id: data.body.trip_id ?? undefined }
      ]
    }),
    updateOtherExpense: builder.mutation<UpdateExpenseResponse, UpdateExpenseParams>({
      query: (arg) => {
        let url = `/v3/other-expenses/${arg.body.id}`;

        if (arg.token) {
          url = `/v3/other-expenses/t/${arg.token}/${arg.body.id}`;
        }

        return {
          url,
          method: 'PUT',
          body: arg.body
        };
      },
      transformResponse: (response: UpdateExpenseResponse) => ({
        data: response.data,
        message: response.message
      }),
      invalidatesTags: (result, error, data) => [
        { type: 'OtherExpenses', id: data.body.id },
        { type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' },
        { type: 'Attachments', id: 'ATTACHMENTS_LIST' }
      ]
    }),
    deleteOtherExpense: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/v3/other-expenses/${id}`,
        method: 'DELETE'
      }),
      onQueryStarted: (id, { dispatch, queryFulfilled }) => {
        const update = dispatch(
          otherExpenseApi.util.updateQueryData('searchOtherExpenses', {}, (expenseData) => {
            const index = expenseData.content.findIndex((e) => e.id === id);
            delete expenseData.content[index];
            expenseData.total -= 1;
          })
        );
        queryFulfilled.catch(() => {
          update.undo();
        });
      },
      transformResponse: (response: { message: string }) => response,
      invalidatesTags: (result, error, id) => [
        { type: 'OtherExpenses', id: id },
        { type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' },
        { type: 'Attachments', id: 'ATTACHMENTS_LIST' }
      ]
    }),
    getExpensesImportTemplate: builder.mutation({
      queryFn: async (arg, api, extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/v3/other-expenses/csv`,
          method: 'GET',
          responseHandler: (response) => response.blob()
        });
        const hiddenElement = document.createElement('a');
        const url = window.URL || window.webkitURL;
        const blobCSV = url.createObjectURL(result.data as Blob);
        hiddenElement.href = blobCSV;
        hiddenElement.target = '_blank';
        hiddenElement.download = `other-expenses-import-template.csv`;
        hiddenElement.click();

        setTimeout(() => {
          if (document.body.contains(hiddenElement)) {
            document.body.removeChild(hiddenElement);
          }
          url.revokeObjectURL(blobCSV);
        }, 100);
        return { data: null };
      }
    }),

    importOtherExpensesCSV: builder.mutation<any, { file: File }>({
      query: ({ file }) => {
        const formData = new FormData();
        const url = '/v3/other-expenses/csv';
        formData.append('file', file);

        return {
          url: url,
          formData: true,
          method: 'POST',
          body: formData
        };
      },
      transformResponse: (response: number) => {
        return response;
      }
    }),

    searchOtherExpenseField: builder.query<
      PaginateResponse<SearchOtherExpenseResponse>,
      SearchFieldParams
    >({
      query: (searchLoadParams) => {
        const { search, field, params } = searchLoadParams;

        return {
          url: '/v3/other-expenses/fields/search',
          method: 'POST',
          body: { search, field },
          params: params
        };
      },
      transformResponse: (response: {
        data: SearchOtherExpenseResponse[];
        total: number;
        more_records: boolean;
      }) => {
        return {
          content: response.data,
          total: response.total,
          more_records: response.more_records
        };
      },
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'OtherExpenses' as const, id })),
              { type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' }
            ]
          : [{ type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' }]
    }),

    exportOtherExpensesCSV: builder.mutation<Blob, OtherExpenseFilterParams>({
      query: (filters) => {
        return {
          url: '/v3/other-expenses/export',
          method: 'POST',
          body: filters,
          responseHandler: (response): any => response.text()
        };
      },
      transformResponse: (response: any) => {
        return new Blob([response]);
      }
    }),

    bulkDeleteOtherExpenses: builder.mutation<{ success: boolean }, OtherExpenseFilterParams>({
      query: (expenseIds) => {
        return {
          url: '/v3/other-expenses/bulk-delete',
          method: 'DELETE',
          body: expenseIds
        };
      },
      transformResponse: (response: any) => {
        return response;
      },
      invalidatesTags: (result, error, id) => [{ type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' }]
    })
  })
});

export const {
  useGetExpenseByIdQuery,
  useSearchOtherExpensesQuery,
  useCreateOtherExpenseMutation,
  useUpdateOtherExpenseMutation,
  useDeleteOtherExpenseMutation,
  useGetExpensesImportTemplateMutation,
  useImportOtherExpensesCSVMutation,
  useSearchOtherExpenseFieldQuery,
  useLazySearchOtherExpenseFieldQuery,
  useExportOtherExpensesCSVMutation,
  useBulkDeleteOtherExpensesMutation
} = otherExpenseApi;
